import { useEffect, useRef, useCallback } from "react";
import { EditorState } from "@codemirror/state";
import { EditorView, keymap, lineNumbers, highlightActiveLine, highlightActiveLineGutter } from "@codemirror/view";
import { defaultKeymap, history, historyKeymap, indentWithTab } from "@codemirror/commands";
import { bracketMatching, indentOnInput, syntaxHighlighting, defaultHighlightStyle } from "@codemirror/language";
import { closeBrackets, closeBracketsKeymap } from "@codemirror/autocomplete";
import { searchKeymap, highlightSelectionMatches } from "@codemirror/search";
import { markdown } from "@codemirror/lang-markdown";
import { leverTheme } from "./lever-theme";
import { latexLanguage } from "./latex-language";
import { ghostText } from "./ghost-text";
import { errorGutterExtension, updateErrors } from "./error-gutter";
import { commentGutterExtension, updateCommentGutter } from "./comment-gutter";
import { useEditorStore } from "../../stores/editor";
import { useUIStore } from "../../stores/ui";
import { useDocumentStore } from "../../stores/document";
import { useCollaborationStore } from "../../stores/collaboration";
import {
  useCompletionStore,
  AGGRESSIVENESS_DEBOUNCE,
  LENGTH_MAX_TOKENS,
} from "../../stores/completion";
import { cn } from "../../lib/cn";

interface LeverEditorProps {
  value: string;
  onChange: (value: string) => void;
  language: "latex" | "markdown" | "docx";
  readOnly?: boolean;
  className?: string;
}

function getLanguageExtension(language: "latex" | "markdown" | "docx") {
  if (language === "markdown") return markdown();
  return latexLanguage;
}

export function LeverEditor({
  value,
  onChange,
  language,
  readOnly = false,
  className,
}: LeverEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const {
    setEditorView,
    setSelection,
    setCursorLine,
    setShowSelectionToolbar,
    setSelectionToolbarPosition,
  } = useEditorStore();

  const saveDocument = useDocumentStore((s) => s.saveDocument);
  const compile = useDocumentStore((s) => s.compile);
  const toolbarAnchor = useUIStore((s) => s.selectionToolbarAnchor);

  const completionEnabled = useCompletionStore((s) => s.enabled);
  const completionAggressiveness = useCompletionStore((s) => s.aggressiveness);
  const completionMaxLength = useCompletionStore((s) => s.maxLength);

  const handleSelectionChange = useCallback(
    (view: EditorView) => {
      const { state } = view;
      const { main } = state.selection;
      const line = state.doc.lineAt(main.head).number;
      setCursorLine(line);

      if (main.from === main.to) {
        setSelection(null);
        setShowSelectionToolbar(false);
        setSelectionToolbarPosition(null);
        return;
      }

      const text = state.sliceDoc(main.from, main.to);
      setSelection({ from: main.from, to: main.to, text });

      const startCoords = view.coordsAtPos(main.from);
      const endCoords = view.coordsAtPos(main.to);
      const containerRect = containerRef.current?.getBoundingClientRect();
      if (startCoords && containerRect) {
        const anchorEnd = toolbarAnchor === "end";
        const midX =
          endCoords && startCoords.top === endCoords.top
            ? startCoords.left + (endCoords.left - startCoords.left) / 2
            : (containerRect.left + containerRect.right) / 2;
        setSelectionToolbarPosition({
          x: midX - containerRect.left,
          y: anchorEnd && endCoords
            ? endCoords.bottom - containerRect.top + 8
            : startCoords.top - containerRect.top - 8,
        });
        setShowSelectionToolbar(true);
      }
    },
    [setCursorLine, setSelection, setShowSelectionToolbar, setSelectionToolbarPosition],
  );

  useEffect(() => {
    if (!containerRef.current) return;

    const saveKeymap = keymap.of([
      {
        key: "Mod-s",
        run: () => {
          saveDocument().then(() => compile()).catch(() => {});
          return true;
        },
      },
      {
        key: "Mod-Enter",
        run: () => {
          saveDocument().then(() => compile()).catch(() => {});
          return true;
        },
      },
    ]);

    const selectionListener = EditorView.updateListener.of((update) => {
      if (update.docChanged) {
        onChangeRef.current(update.state.doc.toString());
      }
      if (update.selectionSet) {
        handleSelectionChange(update.view);
      }
    });

    const state = EditorState.create({
      doc: value,
      extensions: [
        saveKeymap,
        lineNumbers(),
        highlightActiveLineGutter(),
        highlightActiveLine(),
        history(),
        bracketMatching(),
        closeBrackets(),
        indentOnInput(),
        highlightSelectionMatches(),
        keymap.of([
          ...closeBracketsKeymap,
          ...defaultKeymap,
          ...searchKeymap,
          ...historyKeymap,
          indentWithTab,
        ]),
        getLanguageExtension(language),
        syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
        leverTheme,
        ghostText({
          language: language === "docx" ? "docx" : language,
          enabled: completionEnabled,
          debounceMs: AGGRESSIVENESS_DEBOUNCE[completionAggressiveness],
          maxTokens: LENGTH_MAX_TOKENS[completionMaxLength],
        }),
        errorGutterExtension(),
        commentGutterExtension(),
        selectionListener,
        EditorState.readOnly.of(readOnly),
        EditorView.lineWrapping,
        EditorView.contentAttributes.of({ spellcheck: "true" }),
      ],
    });

    const view = new EditorView({
      state,
      parent: containerRef.current,
    });

    viewRef.current = view;
    setEditorView(view);

    return () => {
      view.destroy();
      viewRef.current = null;
      setEditorView(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, readOnly, completionEnabled, completionAggressiveness, completionMaxLength]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;

    const currentDoc = view.state.doc.toString();
    if (currentDoc !== value) {
      view.dispatch({
        changes: { from: 0, to: currentDoc.length, insert: value },
      });
    }
  }, [value]);

  const errors = useDocumentStore((s) => s.errors);
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    updateErrors(view, errors);
  }, [errors]);

  const comments = useCollaborationStore((s) => s.comments);
  const focusedCommentId = useCollaborationStore((s) => s.focusedCommentId);
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    updateCommentGutter(view, comments, focusedCommentId);
  }, [comments, focusedCommentId]);

  return (
    <div
      ref={containerRef}
      className={cn(
        "lever-editor relative h-full w-full [&_.cm-editor]:h-full [&_.cm-editor]:outline-none [&_.cm-scroller]:overflow-auto",
        "[&_.cm-tooltip]:!overflow-visible",
        className,
      )}
    />
  );
}
